import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../interceptors";

// Candidate search over the resume library (GET /ai/resumes) — the same source
// the in-app candidate picker uses. Redesigned to match the app's modern
// surfaces (cards + vf utility classes), replacing the legacy Materialize table.

const PALETTE = [
  "#8B5CF6",
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#EC4899",
  "#84CC16",
];
const colorFor = (key = "") => {
  let h = 0;
  const s = String(key);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
};
const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const EMPTY = { q: "", skill: "", location: "", minYears: "", maxYears: "" };

export default function SearchCandidates() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY);
  const [showFilters, setShowFilters] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (f = filters) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append("size", "50");
      if (f.q) params.append("q", f.q);
      if (f.skill) params.append("skill", f.skill);
      if (f.location) params.append("location", f.location);
      if (f.minYears) params.append("minYears", f.minYears);
      if (f.maxYears) params.append("maxYears", f.maxYears);
      const r = await axiosInstance.get(`/ai/resumes?${params.toString()}`);
      const data = r.data?.content || r.data || [];
      setRows(Array.isArray(data) ? data : []);
      setTotal(
        typeof r.data?.totalElements === "number"
          ? r.data.totalElements
          : Array.isArray(data)
            ? data.length
            : 0,
      );
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.message ||
          "Failed to search candidates.",
      );
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Search candidates - Ample Hire";
    load(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const activeFilterCount = Object.entries(filters).filter(
    ([k, v]) => k !== "q" && v !== "",
  ).length;
  const clear = () => {
    setFilters(EMPTY);
    load(EMPTY);
  };

  return (
    <div className="max-w-[1200px] mx-auto px-2 py-2 font-sans">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-vf-base font-heading">
          Search candidates
        </h1>
        <p className="text-sm text-vf-muted mt-1">
          Find candidates across your resume library by name, skill, location,
          or experience.
        </p>
      </div>

      {/* Filter bar */}
      <div className="card p-0 overflow-hidden mb-5">
        <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-vf-muted"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                className="input pl-9 w-64"
                placeholder="Search name, email, skill…"
                value={filters.q}
                onChange={(e) => setF("q", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
              />
            </div>
            <button onClick={() => load()} className="btn-primary btn-sm">
              Search
            </button>
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`btn-ghost btn-sm flex items-center gap-1.5 ${showFilters ? "text-vf-primary" : ""}`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-vf-primary text-white text-2xs rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={clear}
                className="text-xs text-vf-muted hover:text-vf-danger"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="text-xs text-vf-muted">{total} candidates</div>
        </div>

        {showFilters && (
          <div className="px-5 py-4 border-t bg-vf-tint">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="input-label">Skill</label>
                <input
                  className="input"
                  placeholder="e.g. react, java"
                  value={filters.skill}
                  onChange={(e) => setF("skill", e.target.value)}
                />
              </div>
              <div>
                <label className="input-label">Location</label>
                <input
                  className="input"
                  placeholder="e.g. Bangalore"
                  value={filters.location}
                  onChange={(e) => setF("location", e.target.value)}
                />
              </div>
              <div>
                <label className="input-label">Min experience (yrs)</label>
                <input
                  className="input"
                  type="number"
                  placeholder="0"
                  value={filters.minYears}
                  onChange={(e) => setF("minYears", e.target.value)}
                />
              </div>
              <div>
                <label className="input-label">Max experience (yrs)</label>
                <input
                  className="input"
                  type="number"
                  placeholder="20"
                  value={filters.maxYears}
                  onChange={(e) => setF("maxYears", e.target.value)}
                />
              </div>
              <div className="md:col-span-4 flex justify-end">
                <button onClick={() => load()} className="btn-primary btn-sm">
                  Apply filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="px-5 py-12 text-center">
            <div className="skeleton h-4 w-32 mx-auto rounded" />
          </div>
        ) : error ? (
          <div className="px-5 py-12 text-center text-sm text-vf-danger">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-vf-muted">
            No candidates match your search.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="tbl-header">
                  <th className="text-left px-4 py-3 w-[22%]">Candidate</th>
                  <th className="text-left px-4 py-3 w-[18%]">Role</th>
                  <th className="text-left px-4 py-3 w-[10%]">Experience</th>
                  <th className="text-left px-4 py-3 w-[14%]">Location</th>
                  <th className="text-left px-4 py-3 w-[24%]">Skills</th>
                  <th className="px-4 py-3 w-[12%]"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const skills = Array.isArray(c.skills) ? c.skills : [];
                  return (
                    <tr key={c.id} className="tbl-row">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                            style={{ background: colorFor(c.id ?? c.candidateName) }}
                          >
                            {initials(c.candidateName)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-vf-base truncate">
                              {c.candidateName || "—"}
                            </div>
                            <div className="text-[11px] text-vf-muted truncate">
                              {c.candidateEmail || "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-vf-muted truncate">
                        {c.currentRole || "—"}
                      </td>
                      <td className="px-4 py-3 text-vf-muted">
                        {c.totalYearsExperience != null
                          ? `${c.totalYearsExperience} yrs`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-vf-muted truncate">
                        {c.currentLocation || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {skills.slice(0, 3).map((s, i) => (
                            <span key={i} className="badge-brand text-[10px]">
                              {s}
                            </span>
                          ))}
                          {skills.length > 3 && (
                            <span className="text-[10px] text-vf-muted">
                              +{skills.length - 3}
                            </span>
                          )}
                          {skills.length === 0 && (
                            <span className="text-vf-muted">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() =>
                            navigate(
                              `/admin/talent-pools?action=invite&candidateId=${c.id}`,
                            )
                          }
                          className="btn-primary btn-sm"
                          disabled={!c.candidateEmail}
                          title={
                            c.candidateEmail
                              ? "Invite this candidate"
                              : "No email on file"
                          }
                        >
                          Invite
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
